<script setup lang="ts">
import type { SidebarProps } from "."
import { ref } from "vue"
import { cn } from "@/lib/utils"
import { Sheet, SheetContent } from '@/components/ui/sheet'
import SheetDescription from '@/components/ui/sheet/SheetDescription.vue'
import SheetHeader from '@/components/ui/sheet/SheetHeader.vue'
import SheetTitle from '@/components/ui/sheet/SheetTitle.vue'
import { SIDEBAR_WIDTH_MAX, SIDEBAR_WIDTH_MIN, SIDEBAR_WIDTH_MOBILE, useSidebar } from "./utils"

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<SidebarProps>(), {
  side: "left",
  variant: "sidebar",
  collapsible: "offcanvas",
  resizable: false,
})

const { isMobile, state, openMobile, setOpenMobile, width, setWidth } = useSidebar()
const isResizing = ref(false)
let resizeStartX = 0
let resizeStartWidth = 0

function startResize(event: PointerEvent) {
  if (event.button !== 0 || isMobile.value) return

  event.preventDefault()
  isResizing.value = true
  resizeStartX = event.clientX
  resizeStartWidth = width.value
  ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
}

function resize(event: PointerEvent) {
  if (!isResizing.value) return
  setWidth(resizeStartWidth + event.clientX - resizeStartX)
}

function stopResize(event?: PointerEvent) {
  isResizing.value = false
  const target = event?.currentTarget as HTMLElement | null
  if (event && target?.hasPointerCapture(event.pointerId)) {
    target.releasePointerCapture(event.pointerId)
  }
}

function resizeWithKeyboard(event: KeyboardEvent) {
  const step = event.shiftKey ? 32 : 12

  if (event.key === "ArrowRight") setWidth(width.value + step)
  else if (event.key === "ArrowLeft") setWidth(width.value - step)
  else if (event.key === "Home") setWidth(SIDEBAR_WIDTH_MIN)
  else if (event.key === "End") setWidth(SIDEBAR_WIDTH_MAX)
  else return

  event.preventDefault()
}
</script>

<template>
  <div
    v-if="collapsible === 'none'"
    data-slot="sidebar"
    :class="cn('bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col', props.class)"
    v-bind="$attrs"
  >
    <slot />
  </div>

  <Sheet v-else-if="isMobile" :open="openMobile" v-bind="$attrs" @update:open="setOpenMobile">
    <SheetContent
      data-sidebar="sidebar"
      data-slot="sidebar"
      data-mobile="true"
      :side="side"
      class="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden"
      :style="{
        '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
      }"
    >
      <SheetHeader class="sr-only">
        <SheetTitle>Sidebar</SheetTitle>
        <SheetDescription>Displays the mobile sidebar.</SheetDescription>
      </SheetHeader>
      <div class="flex h-full w-full flex-col">
        <slot />
      </div>
    </SheetContent>
  </Sheet>

  <div
    v-else
    class="group peer text-sidebar-foreground hidden md:block"
    data-slot="sidebar"
    :data-state="state"
    :data-collapsible="state === 'collapsed' ? collapsible : ''"
    :data-variant="variant"
    :data-side="side"
  >
    <!-- This is what handles the sidebar gap on desktop  -->
    <div
      :class="cn(
        'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
        'group-data-[collapsible=offcanvas]:w-0',
        'group-data-[side=right]:rotate-180',
        variant === 'floating' || variant === 'inset'
          ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
          : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
      )"
    />
    <div
      :class="cn(
        'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex',
        side === 'left'
          ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
          : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
        // Adjust the padding for floating and inset variants.
        variant === 'floating' || variant === 'inset'
          ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
          : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
        props.class,
      )"
      :style="{ transitionDuration: isResizing ? '0ms' : undefined }"
      v-bind="$attrs"
    >
      <div
        data-sidebar="sidebar"
        class="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-none"
      >
        <slot />
      </div>
      <div
        v-if="resizable && !isMobile && state === 'expanded'"
        role="separator"
        aria-label="Resize navigation sidebar"
        title="Drag to resize; use the arrow keys to adjust"
        aria-orientation="vertical"
        :aria-valuemin="SIDEBAR_WIDTH_MIN"
        :aria-valuemax="SIDEBAR_WIDTH_MAX"
        :aria-valuenow="width"
        :aria-valuetext="`${width}px`"
        tabindex="0"
        :class="cn(
          'absolute inset-y-2 right-1 z-20 flex w-3 touch-none cursor-col-resize items-center justify-center outline-none after:absolute after:inset-y-0 after:w-0.5 after:rounded-full after:bg-transparent hover:after:bg-sidebar-border focus-visible:after:bg-sidebar-border',
          isResizing && 'after:bg-sidebar-border',
        )"
        :style="{ transitionDuration: isResizing ? '0ms' : undefined }"
        @pointerdown="startResize"
        @pointermove="resize"
        @pointerup="stopResize"
        @pointercancel="stopResize"
        @lostpointercapture="stopResize"
        @keydown="resizeWithKeyboard"
      />
    </div>
  </div>
</template>
